<template>
    <UploadDocumentFrontMobileView
        v-if="isMobile && !noPermissions"
        :translation-key="TRANSLATION_KEY"
        :is-show-welcome-message="isShowWelcomeMessage"
        :no-permissions="noPermissions"
        :loading="loading"
        :is-bad-upload="isBadUpload"
        :is-back="isBack"
        :is-mobile="isMobileLayout"
        :is-photo-upload="isPhotoUpload"
        :show-instructions="showInstructions"
        :show-popup="showPopup"
        :show-help="showHelp"
        :show-how-to-take-picture="showHowToTakePicture"
        :is-securing-connection="isSecuringConnection"
        :is-show-camera="isShowCamera"
        :help-available="helpAvailable"
        :show-custom-notification="showCustomNotification"
        :security-popup="securityPopup"
        :show-loader="showLoader"
        :handle-init-camera="handleInitCamera"
        :handle-show-popup="handleShowPopup"
        :handle-how-to-take-picture="handleHowToTakePicture"
        :show-camera-selection="showCameraSelection"
        :handle-manual="handleManual"
        :handle-security="handleSecurity"
        :handle-upload="handleUpload"
        :handle-back-to-mobile-upload="handleBackToMobileUpload"
        :handle-upload-later="handleUploadLater"
    />
    <UploadDocumentFrontDesktopView
        v-else
        :translation-key="TRANSLATION_KEY"
        :is-bad-upload="isBadUpload"
        :is-back="isBack"
        :is-mobile="isMobileLayout"
        :is-photo-upload="isPhotoUpload"
        :show-help="showHelp"
        :show-popup="showPopup"
        :is-show-camera="isShowCamera"
        :show-loader="showLoader"
        :handle-show-popup="handleShowPopup"
        :handle-back-to-mobile-upload="handleBackToMobileUpload"
        :handle-upload-later="handleUploadLater"
    />
</template>

<script lang="ts">
import SubmitButton from '@/components/basic/buttons/SubmitButton.vue';
import NavigationButton from '@/components/basic/buttons/NavigationButton.vue';
import File from '@/components/basic/fields/File.vue';
import {
    computed,
    defineComponent,
    onMounted,
    ref,
    nextTick,
    onUnmounted,
} from 'vue';
import { useStore } from 'vuex';
import {
    INCREMENT_UPLOAD_DOCUMENT_COUNT,
    SET_PROGRESS,
} from '@/constants/mutation-types';
import UploadError from '../UploadError.vue';
import HowToTakePicture from '@/components/steps/HowToTakePicture.vue';
import SecurityPopup from '@/components/basic/SecurityPopup.vue';
import { UtilityHelper } from '@/helpers/UtilityHelper';
import {
    smartCapture,
    camera,
    gateway,
    fileSystem,
    result,
    initializing,
    view,
} from '@/libraries/websdk_templates';
import { translations } from '@/libraries/translations';
import UploadInstructions from '@/components/UploadInstructions.vue';
import { useRouter } from 'vue-router';
import {
    AmplitudeAnalyticsEvents,
    amplitudeSendEvent,
} from '@/utils/AmplitudeAnalyticsEvents';
import { Action } from '@/constants/ajax';
import i18n from '@/i18n';
import { useGbgLoaderStatus } from '@/composables/useGbgLoaderStatus';
import UploadDocumentFrontDesktopView from './UploadDocumentFrontDesktopView.vue';
import UploadDocumentFrontMobileView from './UploadDocumentFrontMobileView.vue';

declare const GBG: any;

export default defineComponent({
    name: 'UploadDocumentFront',
    components: {
        HowToTakePicture,
        File,
        SubmitButton,
        NavigationButton,
        UploadError,
        SecurityPopup,
        UploadInstructions,
        UtilityHelper,
        UploadDocumentFrontDesktopView,
        UploadDocumentFrontMobileView,
    },
    emits: ['close'],
    setup(_, { emit }) {
        const store = useStore();
        const router = useRouter();
        const TRANSLATION_KEY = 'steps.UploadDocumentFront';
        const params = new URLSearchParams(window.location.search);

        const showHowToTakePicture = ref(true);
        const gbgToken = ref('');
        const clientId = ref('');
        const showLoader = ref(true);
        const isSecuringConnection = ref(false);
        const showHelp = ref(false);
        const isPhotoUpload = ref(false);
        const isShowCamera = ref(false);
        const sdkTranslations = ref<
            (typeof translations)[keyof typeof translations]
        >(translations.en);
        const journeyId = ref('');
        const backendUrl = ref('');
        const isBadUpload = ref(false);
        const helpAvailable = ref(true);
        const manualAvailable = ref(true);
        const showCustomNotification = ref(false);
        const isBack = ref(false);
        const isShowWelcomeMessage = ref(true);
        const noPermissions = ref(false);
        const ignoreFirstJourneyProgress = ref(true);
        const isUploadStartEventSent = ref(false);
        const loading = ref(false);
        const journeyInstance = ref<InstanceType<any> | null>(null);

        const { startGbgLoaderStatus, stopGbgLoaderStatus } =
            useGbgLoaderStatus();
        let cleanup: (() => void) | null = null;

        const TIMEOUT = 45;
        let PROCESS_START: any = null;
        let timeoutId: any = null;
        let PROCESS_ACTIVE = false;
        let requireBackSide = false;
        let attempt = 1;
        let originalGetUserMedia:
            | typeof navigator.mediaDevices.getUserMedia
            | null = null;

        const isMobile = computed(() => store.state.isDetectedMobileDevice);
        const isMobileLayout = computed(
            () => isMobile.value && !noPermissions.value
        );
        const isMobileView = computed(
            () => isMobileLayout.value && !isPhotoUpload.value
        );
        const showPopup = computed(() => store.state.showInstructionPopup);
        const showInstructions = computed(() => store.state.showInstructions);
        const securityPopup = computed(() => store.state.showSecurityPopup);
        const shouldUseFileUploadFlow = computed(
            () => !isMobile.value || noPermissions.value
        );
        const hybridQuery = computed(() => {
            const currentQuery = router.currentRoute.value.query;
            return currentQuery.mobile === 'true' ||
                currentQuery.qrCode === 'true'
                ? currentQuery
                : undefined;
        });

        const getTranslations = () => {
            sdkTranslations.value =
                translations[
                    UtilityHelper.getLocale() as keyof typeof translations
                ];
        };

        const restoreGetUserMedia = () => {
            if (originalGetUserMedia) {
                navigator.mediaDevices.getUserMedia = originalGetUserMedia;
                originalGetUserMedia = null;
            }
        };

        const addRearCameraPreference = (
            constraints: MediaStreamConstraints = {}
        ): MediaStreamConstraints => {
            if (!isMobile.value || constraints.video === false) {
                return constraints;
            }

            if (constraints.video === undefined || constraints.video === true) {
                return {
                    ...constraints,
                    video: {
                        facingMode: { ideal: 'environment' },
                    },
                };
            }

            if (
                typeof constraints.video !== 'object' ||
                constraints.video === null
            ) {
                return constraints;
            }

            const videoConstraints = constraints.video as MediaTrackConstraints;

            if (videoConstraints.deviceId || videoConstraints.facingMode) {
                return constraints;
            }

            return {
                ...constraints,
                video: {
                    ...videoConstraints,
                    facingMode: { ideal: 'environment' },
                },
            };
        };

        const preferRearCamera = () => {
            if (originalGetUserMedia || !navigator.mediaDevices?.getUserMedia) {
                return;
            }

            const nativeGetUserMedia = navigator.mediaDevices.getUserMedia.bind(
                navigator.mediaDevices
            );
            originalGetUserMedia = nativeGetUserMedia;
            navigator.mediaDevices.getUserMedia = (
                constraints?: MediaStreamConstraints
            ) => nativeGetUserMedia(addRearCameraPreference(constraints));
        };

        const handleInitCamera = () => {
            loading.value = true;
            getToken();
        };

        const handleHowToTakePicture = () => {
            isSecuringConnection.value = true;
            store.state.analyticsService.sendStepEvent('howToTakePicture');
            amplitudeSendEvent(
                AmplitudeAnalyticsEvents.proofOfIdentity
                    .PROOF_OF_IDENTITY_HOW_TO_TAKE_PICTURE
            );
            showHowToTakePicture.value = !showHowToTakePicture.value;
            store.state.isGbg = true;
            window.scrollTo(0, 0);
            setTimeout(() => {
                isSecuringConnection.value = false;
            }, 3000);
        };

        function handleJourneyStart() {
            const now = Math.floor(Date.now() / 1000);
            if (!PROCESS_ACTIVE) {
                PROCESS_ACTIVE = true;
                PROCESS_START = now;
                requireBackSide = isBack.value;
            }

            if (timeoutId) clearTimeout(timeoutId);

            timeoutId = setTimeout(() => {
                store.state.ajaxService.sendGBGStatus({
                    type: 1,
                    journeyId: journeyId.value,
                    startTimestamp: PROCESS_START,
                    isTimeout: true,
                    branchCode: i18n.global.locale.value.toUpperCase(),
                    residence: store.state.residenceCountry,
                    requireDocumentSide: requireBackSide ? 'BACK' : 'FRONT',
                    attempt: attempt,
                    scanType: isMobile.value ? 'MOBILE' : 'DESKTOP',
                });
                clearTimeout(timeoutId);
                router.push({
                    name: 'uploadException',
                    query: hybridQuery.value,
                });
            }, TIMEOUT * 1000);
        }

        function handleJourneyEnd() {
            if (!PROCESS_ACTIVE) return;
            const now = Math.floor(Date.now() / 1000);

            fetch(
                `${backendUrl.value}idscanenterprisesvc/journey/get?journeyId=${journeyId.value}`,
                {
                    headers: { Authorization: gbgToken.value },
                }
            )
                .then((response: any) => {
                    response.json().then((data: any) => {
                        const pd = data.ProcessedDocuments;
                        const lastDoc = pd[pd.length - 1];

                        store.state.ajaxService.sendGBGStatus({
                            type: 1,
                            startTimestamp: PROCESS_START,
                            endTimestamp: now,
                            attemptNumber: attempt,
                            journeyId: journeyId.value,
                            isValidated: lastDoc.IsValidated,
                            status: lastDoc.HighLevelResult,
                            branchCode: i18n.global.locale.value.toUpperCase(),
                            residence: store.state.residenceCountry,
                            documentCountry: lastDoc.IssuingCountryCode,
                            documentType: lastDoc.DocumentType,
                            documentSide: lastDoc.DocumentSide,
                            requireDocumentSide: requireBackSide
                                ? 'BACK'
                                : 'FRONT',
                            scanType: isMobile.value ? 'MOBILE' : 'DESKTOP',
                            documentName: lastDoc.DocumentName || null,
                        });

                        PROCESS_START = null;
                        PROCESS_ACTIVE = false;
                        if (timeoutId) {
                            clearTimeout(timeoutId);
                            timeoutId = null;
                            attempt++;
                        }
                    });
                })
                .catch((err: any) => {
                    console.error(err);
                });
        }

        const openHelp = () => {
            const helpButton = document.querySelector('body');
            const eventHandler = (e: any) => {
                const target = e.target as HTMLElement;
                if (target.classList.contains('show-help-button')) {
                    if (
                        document
                            .querySelector('.gbg-camera-container')
                            ?.classList.contains('show-help')
                    ) {
                        showHelp.value = false;
                    } else {
                        showHelp.value = true;
                    }
                }
            };
            if (helpButton) {
                helpButton.addEventListener('click', eventHandler, {
                    passive: true,
                });
            }
        };

        const checkCanvas = () => {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            let blankDetectionInterval: any;

            function check() {
                const videoElement = document.querySelector('video');
                if (videoElement) {
                    canvas.width = videoElement.videoWidth;
                    canvas.height = videoElement.videoHeight;
                    if (context) {
                        context.drawImage(
                            videoElement,
                            0,
                            0,
                            canvas.width,
                            canvas.height
                        );
                        const pixelData = context.getImageData(
                            0,
                            0,
                            canvas.width,
                            canvas.height
                        ).data;
                        const isBlank = pixelData.every((value) => value === 0);
                        if (isBlank) {
                            console.warn(
                                'Camera feed is blank (all black frames).'
                            );
                            store.state.analyticsService.sendStepEvent(
                                'cameraBlankFeedDetected'
                            );
                            amplitudeSendEvent(
                                AmplitudeAnalyticsEvents.proofOfIdentity
                                    .PROOF_OF_IDENTITY_CAMERA_BLANK_FEED_DETECTED
                            );
                            clearInterval(blankDetectionInterval);
                        }
                    }
                }
            }
            blankDetectionInterval = setInterval(check, 1000);
        };

        const handleAttemptsButton = () => {
            const body = document.querySelector('body') as HTMLBodyElement;
            const eventHandler = (e: any) => {
                const target = e.target as HTMLElement;

                if (
                    target.matches(
                        '.help-button-wrapper, .help-button-wrapper span, .help-button-wrapper p'
                    )
                ) {
                    const notificationBlue =
                        document.querySelector('.notification-blue');

                    if (notificationBlue) {
                        (notificationBlue as HTMLElement).style.display =
                            'none';
                    }
                }
                if (
                    target.matches(
                        '.control__switch, .control__switch *, .manual--mode--switch, .manual--mode--switch *'
                    )
                ) {
                    body.classList.add('hide-notification');
                    const interval = setInterval(() => {
                        const notificationBlue =
                            document.querySelector('.notification-blue');

                        if (notificationBlue) {
                            (notificationBlue as HTMLElement).style.display =
                                'none';
                            body.classList.remove('hide-notification');
                            clearInterval(interval);
                        }
                    }, 1);
                }
            };
            if (body) {
                body.addEventListener('click', eventHandler);
            }
        };

        const handlePreviewStepButtons = () => {
            const eventHandler = (e: MouseEvent) => {
                const target = e.target as HTMLElement;

                const uploadButton = target.closest(
                    '[data-jcs-element="view__upload"]'
                );
                const retryButton = target.closest(
                    '[data-jcs-element="view__retry"]'
                );

                if (uploadButton) {
                    amplitudeSendEvent(
                        AmplitudeAnalyticsEvents.webSdk
                            .WEBSDK_PREVIEW_STEP_CONFIRM_BUTTON
                    );
                }

                if (retryButton) {
                    amplitudeSendEvent(
                        AmplitudeAnalyticsEvents.webSdk
                            .WEBSDK_PREVIEW_STEP_RETRY_BUTTON
                    );
                }
            };

            document.body.addEventListener('click', eventHandler);

            return () => {
                document.body.removeEventListener('click', eventHandler);
            };
        };

        const sendWebSdkStartEvent = () => {
            if (
                params.get('mobile') === 'true' ||
                params.get('qrCode') === 'true'
            ) {
                amplitudeSendEvent(
                    AmplitudeAnalyticsEvents.webSdk.WEBSDK_HYBRID_UPLOAD_START
                );
            } else if (isMobile.value) {
                amplitudeSendEvent(
                    AmplitudeAnalyticsEvents.webSdk.WEBSDK_MOBILE_UPLOAD_START
                );
            } else {
                amplitudeSendEvent(
                    AmplitudeAnalyticsEvents.webSdk.WEBSDK_DESKTOP_UPLOAD_START
                );
            }
        };

        const gbgSDK = async () => {
            if (!isUploadStartEventSent.value) {
                sendWebSdkStartEvent();
                isUploadStartEventSent.value = true;
            }

            const onJourneyEventPro = (
                event: string,
                meta: any,
                state: any
            ) => {
                switch (event) {
                    case GBG.Idscan.JourneyEvent.DISPLAY_STEP:
                        if (
                            state.inputProvider == 'FILESYSTEM' &&
                            state.journey.currentResult == 'Failed'
                        ) {
                            isBadUpload.value = true;
                        } else {
                            isBadUpload.value = false;
                        }
                        break;
                    case GBG.Idscan.JourneyEvent.TRANSFER_STARTED:
                        showLoader.value = true;
                        if (!ignoreFirstJourneyProgress.value) {
                            handleJourneyStart();
                            startGbgLoaderStatus();
                        }
                        break;
                    case GBG.Idscan.JourneyEvent.JOURNEY_CANCEL:
                        notifyJourney(journeyId.value, event);
                        break;
                    case GBG.Idscan.JourneyEvent.JOURNEY_CLEAR:
                        notifyJourney(journeyId.value, event);
                        break;
                    case GBG.Idscan.JourneyEvent.JOURNEY_PROGRESS:
                        if (!ignoreFirstJourneyProgress.value) {
                            notifyJourney(journeyId.value, event);
                        }
                        ignoreFirstJourneyProgress.value = false;
                        if (state.action == 'IDENTITY:BACK') {
                            isBack.value = true;
                        } else {
                            isBack.value = false;
                        }
                        break;
                    case GBG.Idscan.JourneyEvent.JOURNEY_START:
                        notifyJourney(journeyId.value, event);
                        break;
                    case GBG.Idscan.JourneyEvent.JOURNEY_END:
                        store.state.isGbg = false;
                        store.state.isUploadDocumentStep = false;
                        store.state.showLongDisclaimer = false;
                        store.state.isLocked = 'active';
                        if (
                            isMobile.value === false &&
                            journeyId.value.length <= 0
                        ) {
                            setTimeout(() => {
                                amplitudeSendEvent(
                                    AmplitudeAnalyticsEvents.webSdk
                                        .WEBSDK_DESKTOP_UPLOAD_FINISH
                                );
                                store.commit(INCREMENT_UPLOAD_DOCUMENT_COUNT);
                                router.push({ name: 'uploadFailed' });
                                store.state.isLocked = '';
                            }, 10000);
                        } else {
                            setTimeout(() => {
                                fetch(
                                    `${backendUrl.value}idscanenterprisesvc/journey/get?journeyId=${journeyId.value}`,
                                    {
                                        headers: {
                                            Authorization: gbgToken.value,
                                        },
                                    }
                                )
                                    .then((response: any) => {
                                        response.json().then((data: any) => {
                                            store.state.clientIdData =
                                                data.ProcessedDocuments;
                                            store.state.isSubheaderVisible = true;
                                            const journeyStatus =
                                                data.HighLevelResult.toLowerCase();
                                            store.state.isLocked = '';

                                            store.state.ajaxService.sendGBGStatus(
                                                {
                                                    type: 2,
                                                    journeyId: journeyId.value,
                                                    journeyStatus:
                                                        journeyStatus,
                                                    scanType: isMobile.value
                                                        ? 'MOBILE'
                                                        : 'DESKTOP',
                                                }
                                            );

                                            if (
                                                journeyInstance.value?.terminate
                                            ) {
                                                journeyInstance.value.terminate();
                                            }

                                            journeyInstance.value = null;

                                            if (
                                                params.get('mobile') ===
                                                    'true' ||
                                                params.get('qrCode') === 'true'
                                            ) {
                                                amplitudeSendEvent(
                                                    AmplitudeAnalyticsEvents
                                                        .webSdk
                                                        .WEBSDK_HYBRID_UPLOAD_FINISH
                                                );
                                                router.push({
                                                    name: 'mobileReturnToDesktop',
                                                });
                                            } else {
                                                if (isMobile.value) {
                                                    amplitudeSendEvent(
                                                        AmplitudeAnalyticsEvents
                                                            .webSdk
                                                            .WEBSDK_MOBILE_UPLOAD_FINISH
                                                    );
                                                } else {
                                                    amplitudeSendEvent(
                                                        AmplitudeAnalyticsEvents
                                                            .webSdk
                                                            .WEBSDK_DESKTOP_UPLOAD_FINISH
                                                    );
                                                }

                                                if (
                                                    journeyStatus === 'passed'
                                                ) {
                                                    router.push({
                                                        name: 'uploadSucceeded',
                                                    });
                                                } else {
                                                    store.commit(
                                                        INCREMENT_UPLOAD_DOCUMENT_COUNT
                                                    );
                                                    router.push({
                                                        name: 'uploadFailed',
                                                    });
                                                }
                                            }
                                        });
                                    })
                                    .catch((err: any) => {
                                        console.error(err);
                                    });
                            }, 1000);
                        }
                        notifyJourney(journeyId.value, event);
                        break;
                    case GBG.Idscan.JourneyEvent.TRANSFER_COMPLETE:
                        showLoader.value = false;
                        if (!ignoreFirstJourneyProgress.value) {
                            handleJourneyEnd();
                            stopGbgLoaderStatus();
                        }
                        break;
                    case GBG.Idscan.JourneyEvent.TRANSFER_FAILED:
                        showLoader.value = false;
                        stopGbgLoaderStatus();
                }
            };

            const baseTemplate = {
                smartCapture: {
                    type: 'function',
                    processor: 'mustache',
                    provider: function () {
                        return smartCapture();
                    },
                },
                camera: {
                    type: 'function',
                    processor: 'mustache',
                    provider: function () {
                        return camera();
                    },
                },
                gateway: {
                    type: 'function',
                    processor: 'mustache',
                    provider: function () {
                        return gateway();
                    },
                },
                filesystem: {
                    type: 'function',
                    processor: 'mustache',
                    provider: function () {
                        return fileSystem();
                    },
                },
                view: {
                    type: 'function',
                    processor: 'mustache',
                    provider: function () {
                        return view(isMobileView.value);
                    },
                },
                result: {
                    type: 'function',
                    processor: 'mustache',
                    provider: function () {
                        return result();
                    },
                },
            };

            const overrideTemplate = shouldUseFileUploadFlow.value
                ? {
                      ...baseTemplate,
                      initializing: {
                          type: 'function',
                          processor: 'mustache',
                          provider: function () {
                              return initializing();
                          },
                      },
                  }
                : baseTemplate;

            const instance = new (window as any).GBG.Idscan.JourneyContainer({
                backendUrl: `${backendUrl.value}`,
                container: '#gbgContainer',
                token: gbgToken.value,
                JourneyId: `"${journeyId.value}"`,
                onJourneyEvent: onJourneyEventPro,
                journeyDefinitionId: '887fa09f-e942-4d11-b937-84e8e4263415',
                smartCapture: {
                    autoStart: true,
                    timeout: 5000,
                    workerScriptUrl:
                        process.env.BASE_URL +
                        'idscan/idscan-jcs.ides-micro.d07be1731e065d5d706b.js',
                    asmScriptUrl: 'idesmicro_asm.js',
                },
                manualCapture: {
                    enabled: true,
                    timeout: 15,
                },
                dictionary: sdkTranslations.value,
                hideAutoCaptureHints: false,
                isVerboseLogEnabled: true,
                templates: overrideTemplate,
                additionalData: [
                    {
                        Name: 'TMSClientId',
                        Value: clientId.value,
                    },
                ],
                assetsDirectory: process.env.BASE_URL + 'assets',

                ...(shouldUseFileUploadFlow.value
                    ? { defaultInputProvider: 'FILESYSTEM' }
                    : {
                          previewPages: {
                              documentStep: {
                                  smartCapture: true,
                                  manualCapture: true,
                              },
                          },
                      }),
            });

            journeyInstance.value = instance;
            await instance.initialize();
        };

        const checkCameraPermissions = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                });
                stream.getTracks().forEach((track) => track.stop());
                showCustomNotification.value = true;
                isShowWelcomeMessage.value = false;
                await nextTick();
                gbgSDK();
            } catch (error) {
                console.error('Error in checkCameraPermissions:', error);
                noPermissions.value = true;
                isShowWelcomeMessage.value = false;
                showCustomNotification.value = false;
                blockCameraPermission();
                store.state.analyticsService.sendStepEvent(
                    'cameraPermissionDenied'
                );
                amplitudeSendEvent(
                    AmplitudeAnalyticsEvents.proofOfIdentity
                        .PROOF_OF_IDENTITY_CAMERA_PERMISSION_DENIED
                );
                await nextTick();
                gbgSDK();
            }
        };

        const handleShowHelp = () => {
            const body = document.querySelector('body') as HTMLBodyElement;
            const eventHandler = (e: any) => {
                const target = e.target as HTMLElement;
                if (
                    target.matches(
                        '.notification-photo-help, .notification-photo-help span, .notification-photo-help p'
                    )
                ) {
                    store.state.showInstructionPopup = true;
                    showHelp.value = false;
                }
            };
            if (body) {
                body.addEventListener('click', eventHandler);
            }
        };

        const handleManual = () => {
            const manualSwitch = document.querySelector(
                'body .manual--mode--switch'
            );
            if (manualSwitch) {
                const manualSwitchInput = manualSwitch.querySelector('input');
                if (manualSwitchInput) {
                    (manualSwitchInput as HTMLElement)?.click();
                    manualAvailable.value = true;
                    showHelp.value = false;
                }
            }
        };

        const handleShowPopup = () => {
            store.state.showInstructionPopup = true;
            showHelp.value = false;
        };

        const showCameraSelection = () => {
            isShowCamera.value = true;
            showHelp.value = false;
            const div = document.querySelector(
                'body .dark-inner-provider-container'
            );
            if (div) {
                div.scroll({ top: 0, behavior: 'smooth' });
            }
        };

        const notifyJourney = async (
            journeyId: string,
            journeyStatus: string
        ): Promise<any> => {
            const ajaxService = store.state.ajaxService;
            try {
                const data = await ajaxService.post(
                    ajaxService.RESOURCES[Action.NOTIFY_JOURNEY],
                    {
                        token: ajaxService.getToken(),
                        journeyStatus: journeyStatus,
                        journeyId: journeyId,
                    }
                );
                if (data.status === 'error') {
                    store.state.globalError = data.errors;
                }
            } catch (error) {
                store.state.globalError = error;
            }
        };

        const handleSecurity = () => {
            store.state.showSecurityPopup = true;
            showHelp.value = false;
        };

        const handleUpload = () => {
            const photoUploadButton = document.querySelector(
                'body .upload-photos-button'
            ) as HTMLElement | null;

            if (photoUploadButton) {
                photoUploadButton.click();
                showCustomNotification.value = false;
                showHelp.value = false;
                store.state.isGbg = false;
                isPhotoUpload.value = true;
            }
        };

        const closeHelp = () => {
            showHelp.value = false;
        };

        const getToken = async () => {
            const ajaxService = store.state.ajaxService;
            let retryCount = 0;
            const maxRetries = 4;
            const fetchToken = async () => {
                try {
                    const data = await ajaxService.post(
                        ajaxService.RESOURCES[Action.GET_IDSCAN_TOKEN],
                        {
                            token: ajaxService.getToken(),
                        }
                    );

                    if (data.status === 'success') {
                        gbgToken.value = data.gbgToken;
                        journeyId.value = data.journeyId;
                        clientId.value = data.clientId;
                        backendUrl.value = data.backendUrl;
                        if (isMobile.value) {
                            await checkCameraPermissions();
                        } else {
                            gbgSDK();
                        }
                        return data;
                    } else {
                        if (retryCount < maxRetries) {
                            retryCount++;
                            setTimeout(fetchToken, 3000);
                        } else {
                            router.push({
                                name: 'uploadException',
                                query: hybridQuery.value,
                            });
                        }
                    }
                } catch (error) {
                    if (retryCount < maxRetries) {
                        retryCount++;
                        setTimeout(fetchToken, 3000);
                    } else {
                        router.push({
                            name: 'uploadException',
                            query: hybridQuery.value,
                        });
                    }
                }
            };

            await fetchToken();
        };

        const checkWaitStatus = () => {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('qrCode') === 'true') {
                const ajaxService = store.state.ajaxService;
                const interval = setInterval(async () => {
                    const data = await ajaxService.post(
                        ajaxService.RESOURCES[Action.MOBILE_QR_CODE_SCAN],
                        {
                            token: ajaxService.getToken(),
                            qrCodeScan: true,
                        }
                    );
                    if (data.status === 'success') {
                        clearInterval(interval);
                    }
                }, 1000);

                setTimeout(() => {
                    clearInterval(interval);
                }, 10000);
            }
        };

        const blockCameraPermission = () => {
            if (!navigator.mediaDevices?.getUserMedia) {
                return;
            }

            if (!originalGetUserMedia) {
                originalGetUserMedia = navigator.mediaDevices.getUserMedia.bind(
                    navigator.mediaDevices
                );
            }

            // Override getUserMedia to block camera access
            navigator.mediaDevices.getUserMedia = function () {
                return Promise.reject(new Error('NotAllowedError'));
            };
        };

        const handleBackToMobileUpload = (): void => {
            emit('close');
            store.dispatch('ajax', {
                data: {
                    mobileDesktopChoice: 1,
                },
                action: Action.SELECT_MOBILE_DESKTOP,
            });
        };

        const handleUploadLater = async () => {
            await store.state.navigationService.goToRoute(
                'personalDataConfirm'
            );
        };

        onMounted(() => {
            window.scrollTo(0, 0);
            store.commit(SET_PROGRESS, 6);
            getTranslations();
            store.state.isUploadDocumentStep = true;

            if (isMobile.value) {
                preferRearCamera();
                openHelp();
                handleAttemptsButton();
                handleShowHelp();
                checkCanvas();
                cleanup = handlePreviewStepButtons();
            } else {
                blockCameraPermission();
                getToken();
            }

            checkWaitStatus();
        });

        onUnmounted(() => {
            store.state.isGbg = false;
            cleanup?.();
            restoreGetUserMedia();
        });

        return {
            TRANSLATION_KEY,
            UtilityHelper,
            showLoader,
            isPhotoUpload,
            showHelp,
            showCameraSelection,
            helpAvailable,
            isShowWelcomeMessage,
            noPermissions,
            showInstructions,
            isBadUpload,
            showHowToTakePicture,
            handleHowToTakePicture,
            isShowCamera,
            securityPopup,
            showPopup,
            handleSecurity,
            handleUpload,
            isSecuringConnection,
            handleShowPopup,
            handleManual,
            closeHelp,
            showCustomNotification,
            isBack,
            loading,
            handleInitCamera,
            isMobile,
            isMobileLayout,
            gbgToken,
            clientId,
            journeyId,
            backendUrl,
            getToken,
            gbgSDK,
            HowToTakePicture,
            handleBackToMobileUpload,
            handleUploadLater,
        };
    },
});
</script>
